import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import {
  formatValidationErrorsFromBody,
  parseServerError,
} from "utils/errors";

const emptySelects = {
  recyclingCodes: [],
  componentTypes: [],
  materialFamilies: [],
  plasticForms: [],
  separateAbilities: [],
  sortingInstructions: [],
  reusabilityTypes: [],
  weightUnits: [],
  labelStatuses: [],
  representativeTypes: [],
  materialSubtypesByFamily: {},
};

const initialState = {
  labels: [],
  currentLabel: null,

  countries: [],
  components: [],
  selects: { ...emptySelects },

  fetchingLabels: false,
  fetchingLabel: false,
  creatingLabel: false,
  updatingLabel: false,
  deletingLabel: false,

  fetchingCountries: false,
  creatingCountry: false,
  updatingCountry: false,
  deletingCountry: false,

  fetchingComponents: false,
  creatingComponent: false,
  updatingComponent: false,
  deletingComponent: false,

  fetchingSelects: false,
  selectsLoaded: false,

  labelsError: null,
  labelError: null,
  countriesError: null,
  componentsError: null,
  selectsError: null,
};

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function upsertById(list, item) {
  if (!item || item.id == null) return list;
  const index = list.findIndex((entry) => String(entry.id) === String(item.id));
  if (index === -1) return [...list, item];
  const next = list.slice();
  next[index] = { ...list[index], ...item };
  return next;
}

function removeById(list, id) {
  return list.filter((entry) => String(entry.id) !== String(id));
}

async function readApiError(response, fallback) {
  try {
    const body = await response.json();
    const fromValidation = formatValidationErrorsFromBody(body);
    if (fromValidation) return fromValidation;
    const parsed = parseServerError(body);
    if (parsed) {
      return typeof parsed === "string" ? parsed : String(parsed);
    }
    if (typeof body === "string" && body.trim() !== "") return body;
  } catch {
  }
  return response.statusText || fallback;
}

async function requestJson(endpoint, method, payload, fallbackError) {
  const isFormData =
    typeof FormData !== "undefined" && payload instanceof FormData;
  const response = await callApi(
    endpoint,
    method,
    method === "GET" ? {} : payload ?? {},
    isFormData ? { contentType: "multipart/form-data" } : undefined
  );
  if (!response.ok) {
    const message = await readApiError(response, fallbackError);
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export const fetchLabelSettingsSelects = createAsyncThunk(
  "labelSettings/fetchLabelSettingsSelects",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/selects",
        "GET",
        undefined,
        "Failed to fetch label settings selects"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch label settings selects"
      );
    }
  }
);

export const fetchPackagingLabels = createAsyncThunk(
  "labelSettings/fetchPackagingLabels",
  async (_, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/labels",
        "GET",
        undefined,
        "Failed to fetch packaging labels"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging labels"
      );
    }
  }
);

export const fetchPackagingLabel = createAsyncThunk(
  "labelSettings/fetchPackagingLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}`,
        "GET",
        undefined,
        "Failed to fetch packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging label"
      );
    }
  }
);

export const createPackagingLabel = createAsyncThunk(
  "labelSettings/createPackagingLabel",
  async (payload, { rejectWithValue }) => {
    try {
      return await requestJson(
        "/label-settings/labels",
        "POST",
        payload ?? {},
        "Failed to create packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to create packaging label"
      );
    }
  }
);

export const updatePackagingLabel = createAsyncThunk(
  "labelSettings/updatePackagingLabel",
  async ({ labelId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging label"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update packaging label"
      );
    }
  }
);

export const deletePackagingLabel = createAsyncThunk(
  "labelSettings/deletePackagingLabel",
  async (labelId, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging label"
      );
      return { success: true, id: labelId };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to delete packaging label"
      );
    }
  }
);

export const fetchPackagingLabelCountries = createAsyncThunk(
  "labelSettings/fetchPackagingLabelCountries",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries`,
        "GET",
        undefined,
        "Failed to fetch packaging label countries"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging label countries"
      );
    }
  }
);

export const createPackagingLabelCountry = createAsyncThunk(
  "labelSettings/createPackagingLabelCountry",
  async ({ labelId, payload }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries`,
        "POST",
        payload ?? {},
        "Failed to create packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to create packaging label country"
      );
    }
  }
);

export const fetchPackagingLabelCountry = createAsyncThunk(
  "labelSettings/fetchPackagingLabelCountry",
  async ({ labelId, countryId }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "GET",
        undefined,
        "Failed to fetch packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging label country"
      );
    }
  }
);

export const updatePackagingLabelCountry = createAsyncThunk(
  "labelSettings/updatePackagingLabelCountry",
  async ({ labelId, countryId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging label country"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update packaging label country"
      );
    }
  }
);

export const deletePackagingLabelCountry = createAsyncThunk(
  "labelSettings/deletePackagingLabelCountry",
  async ({ labelId, countryId }, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}/countries/${countryId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging label country"
      );
      return { success: true, id: countryId };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to delete packaging label country"
      );
    }
  }
);

export const fetchPackagingComponents = createAsyncThunk(
  "labelSettings/fetchPackagingComponents",
  async (labelId, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components`,
        "GET",
        undefined,
        "Failed to fetch packaging components"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging components"
      );
    }
  }
);

export const createPackagingComponent = createAsyncThunk(
  "labelSettings/createPackagingComponent",
  async ({ labelId, payload }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components`,
        "POST",
        payload ?? {},
        "Failed to create packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to create packaging component"
      );
    }
  }
);

export const fetchPackagingComponent = createAsyncThunk(
  "labelSettings/fetchPackagingComponent",
  async ({ labelId, componentId }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "GET",
        undefined,
        "Failed to fetch packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch packaging component"
      );
    }
  }
);

export const updatePackagingComponent = createAsyncThunk(
  "labelSettings/updatePackagingComponent",
  async ({ labelId, componentId, changes }, { rejectWithValue }) => {
    try {
      return await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "POST",
        changes ?? {},
        "Failed to update packaging component"
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update packaging component"
      );
    }
  }
);

export const deletePackagingComponent = createAsyncThunk(
  "labelSettings/deletePackagingComponent",
  async ({ labelId, componentId }, { rejectWithValue }) => {
    try {
      await requestJson(
        `/label-settings/labels/${labelId}/components/${componentId}`,
        "DELETE",
        undefined,
        "Failed to delete packaging component"
      );
      return { success: true, id: componentId };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to delete packaging component"
      );
    }
  }
);

const labelSettingsSlice = createSlice({
  name: "labelSettings",
  initialState,
  reducers: {
    clearLabelsError: (state) => {
      state.labelsError = null;
    },
    clearLabelError: (state) => {
      state.labelError = null;
    },
    clearCountriesError: (state) => {
      state.countriesError = null;
    },
    clearComponentsError: (state) => {
      state.componentsError = null;
    },
    clearSelectsError: (state) => {
      state.selectsError = null;
    },
    clearCurrentLabel: (state) => {
      state.currentLabel = null;
      state.countries = [];
      state.components = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabelSettingsSelects.pending, (state) => {
        state.fetchingSelects = true;
        state.selectsError = null;
      })
      .addCase(fetchLabelSettingsSelects.fulfilled, (state, action) => {
        state.fetchingSelects = false;
        const payload =
          action.payload && typeof action.payload === "object"
            ? action.payload
            : {};
        const materialSubtypesByFamily =
          payload.materialSubtypesByFamily &&
          typeof payload.materialSubtypesByFamily === "object" &&
          !Array.isArray(payload.materialSubtypesByFamily)
            ? payload.materialSubtypesByFamily
            : {};
        state.selects = {
          recyclingCodes: Array.isArray(payload.recyclingCodes)
            ? payload.recyclingCodes
            : [],
          componentTypes: Array.isArray(payload.componentTypes)
            ? payload.componentTypes
            : [],
          materialFamilies: Array.isArray(payload.materialFamilies)
            ? payload.materialFamilies
            : [],
          plasticForms: Array.isArray(payload.plasticForms)
            ? payload.plasticForms
            : [],
          separateAbilities: Array.isArray(payload.separateAbilities)
            ? payload.separateAbilities
            : [],
          sortingInstructions: Array.isArray(payload.sortingInstructions)
            ? payload.sortingInstructions
            : [],
          reusabilityTypes: Array.isArray(payload.reusabilityTypes)
            ? payload.reusabilityTypes
            : [],
          weightUnits: Array.isArray(payload.weightUnits)
            ? payload.weightUnits
            : [],
          labelStatuses: Array.isArray(payload.labelStatuses)
            ? payload.labelStatuses
            : [],
          representativeTypes: Array.isArray(payload.representativeTypes)
            ? payload.representativeTypes
            : [],
          materialSubtypesByFamily,
        };
        state.selectsLoaded = true;
        state.selectsError = null;
      })
      .addCase(fetchLabelSettingsSelects.rejected, (state, action) => {
        state.fetchingSelects = false;
        state.selectsError =
          action.payload || "Failed to fetch label settings selects";
      })

      .addCase(fetchPackagingLabels.pending, (state) => {
        state.fetchingLabels = true;
        state.labelsError = null;
      })
      .addCase(fetchPackagingLabels.fulfilled, (state, action) => {
        state.fetchingLabels = false;
        state.labels = asList(action.payload);
        state.labelsError = null;
      })
      .addCase(fetchPackagingLabels.rejected, (state, action) => {
        state.fetchingLabels = false;
        state.labelsError =
          action.payload || "Failed to fetch packaging labels";
      })

      .addCase(fetchPackagingLabel.pending, (state) => {
        state.fetchingLabel = true;
        state.labelError = null;
      })
      .addCase(fetchPackagingLabel.fulfilled, (state, action) => {
        state.fetchingLabel = false;
        state.currentLabel = action.payload || null;
        if (action.payload) {
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(fetchPackagingLabel.rejected, (state, action) => {
        state.fetchingLabel = false;
        state.labelError =
          action.payload || "Failed to fetch packaging label";
      })

      .addCase(createPackagingLabel.pending, (state) => {
        state.creatingLabel = true;
        state.labelError = null;
      })
      .addCase(createPackagingLabel.fulfilled, (state, action) => {
        state.creatingLabel = false;
        state.currentLabel = action.payload || null;
        if (action.payload) {
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(createPackagingLabel.rejected, (state, action) => {
        state.creatingLabel = false;
        state.labelError =
          action.payload || "Failed to create packaging label";
      })

      .addCase(updatePackagingLabel.pending, (state) => {
        state.updatingLabel = true;
        state.labelError = null;
      })
      .addCase(updatePackagingLabel.fulfilled, (state, action) => {
        state.updatingLabel = false;
        if (action.payload) {
          if (
            state.currentLabel &&
            String(state.currentLabel.id) === String(action.payload.id)
          ) {
            state.currentLabel = { ...state.currentLabel, ...action.payload };
          } else if (
            state.currentLabel &&
            String(state.currentLabel.id) ===
              String(action.meta?.arg?.labelId)
          ) {
            state.currentLabel = { ...state.currentLabel, ...action.payload };
          }
          state.labels = upsertById(state.labels, action.payload);
        }
        state.labelError = null;
      })
      .addCase(updatePackagingLabel.rejected, (state, action) => {
        state.updatingLabel = false;
        state.labelError =
          action.payload || "Failed to update packaging label";
      })

      .addCase(deletePackagingLabel.pending, (state) => {
        state.deletingLabel = true;
        state.labelError = null;
      })
      .addCase(deletePackagingLabel.fulfilled, (state, action) => {
        state.deletingLabel = false;
        const deletedId = action.payload?.id ?? action.meta?.arg;
        state.labels = removeById(state.labels, deletedId);
        if (
          state.currentLabel &&
          String(state.currentLabel.id) === String(deletedId)
        ) {
          state.currentLabel = null;
          state.countries = [];
          state.components = [];
        }
        state.labelError = null;
      })
      .addCase(deletePackagingLabel.rejected, (state, action) => {
        state.deletingLabel = false;
        state.labelError =
          action.payload || "Failed to delete packaging label";
      })

      .addCase(fetchPackagingLabelCountries.pending, (state) => {
        state.fetchingCountries = true;
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountries.fulfilled, (state, action) => {
        state.fetchingCountries = false;
        state.countries = asList(action.payload);
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountries.rejected, (state, action) => {
        state.fetchingCountries = false;
        state.countriesError =
          action.payload || "Failed to fetch packaging label countries";
      })

      .addCase(createPackagingLabelCountry.pending, (state) => {
        state.creatingCountry = true;
        state.countriesError = null;
      })
      .addCase(createPackagingLabelCountry.fulfilled, (state, action) => {
        state.creatingCountry = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(createPackagingLabelCountry.rejected, (state, action) => {
        state.creatingCountry = false;
        state.countriesError =
          action.payload || "Failed to create packaging label country";
      })

      .addCase(fetchPackagingLabelCountry.pending, (state) => {
        state.fetchingCountries = true;
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountry.fulfilled, (state, action) => {
        state.fetchingCountries = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(fetchPackagingLabelCountry.rejected, (state, action) => {
        state.fetchingCountries = false;
        state.countriesError =
          action.payload || "Failed to fetch packaging label country";
      })

      .addCase(updatePackagingLabelCountry.pending, (state) => {
        state.updatingCountry = true;
        state.countriesError = null;
      })
      .addCase(updatePackagingLabelCountry.fulfilled, (state, action) => {
        state.updatingCountry = false;
        if (action.payload) {
          state.countries = upsertById(state.countries, action.payload);
        }
        state.countriesError = null;
      })
      .addCase(updatePackagingLabelCountry.rejected, (state, action) => {
        state.updatingCountry = false;
        state.countriesError =
          action.payload || "Failed to update packaging label country";
      })

      .addCase(deletePackagingLabelCountry.pending, (state) => {
        state.deletingCountry = true;
        state.countriesError = null;
      })
      .addCase(deletePackagingLabelCountry.fulfilled, (state, action) => {
        state.deletingCountry = false;
        const deletedId =
          action.payload?.id ?? action.meta?.arg?.countryId;
        state.countries = removeById(state.countries, deletedId);
        state.countriesError = null;
      })
      .addCase(deletePackagingLabelCountry.rejected, (state, action) => {
        state.deletingCountry = false;
        state.countriesError =
          action.payload || "Failed to delete packaging label country";
      })

      .addCase(fetchPackagingComponents.pending, (state) => {
        state.fetchingComponents = true;
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponents.fulfilled, (state, action) => {
        state.fetchingComponents = false;
        state.components = asList(action.payload);
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponents.rejected, (state, action) => {
        state.fetchingComponents = false;
        state.componentsError =
          action.payload || "Failed to fetch packaging components";
      })

      .addCase(createPackagingComponent.pending, (state) => {
        state.creatingComponent = true;
        state.componentsError = null;
      })
      .addCase(createPackagingComponent.fulfilled, (state, action) => {
        state.creatingComponent = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(createPackagingComponent.rejected, (state, action) => {
        state.creatingComponent = false;
        state.componentsError =
          action.payload || "Failed to create packaging component";
      })

      .addCase(fetchPackagingComponent.pending, (state) => {
        state.fetchingComponents = true;
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponent.fulfilled, (state, action) => {
        state.fetchingComponents = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(fetchPackagingComponent.rejected, (state, action) => {
        state.fetchingComponents = false;
        state.componentsError =
          action.payload || "Failed to fetch packaging component";
      })

      .addCase(updatePackagingComponent.pending, (state) => {
        state.updatingComponent = true;
        state.componentsError = null;
      })
      .addCase(updatePackagingComponent.fulfilled, (state, action) => {
        state.updatingComponent = false;
        if (action.payload) {
          state.components = upsertById(state.components, action.payload);
        }
        state.componentsError = null;
      })
      .addCase(updatePackagingComponent.rejected, (state, action) => {
        state.updatingComponent = false;
        state.componentsError =
          action.payload || "Failed to update packaging component";
      })

      .addCase(deletePackagingComponent.pending, (state) => {
        state.deletingComponent = true;
        state.componentsError = null;
      })
      .addCase(deletePackagingComponent.fulfilled, (state, action) => {
        state.deletingComponent = false;
        const deletedId =
          action.payload?.id ?? action.meta?.arg?.componentId;
        state.components = removeById(state.components, deletedId);
        state.componentsError = null;
      })
      .addCase(deletePackagingComponent.rejected, (state, action) => {
        state.deletingComponent = false;
        state.componentsError =
          action.payload || "Failed to delete packaging component";
      });
  },
});

export const {
  clearLabelsError,
  clearLabelError,
  clearCountriesError,
  clearComponentsError,
  clearSelectsError,
  clearCurrentLabel,
} = labelSettingsSlice.actions;

export default labelSettingsSlice.reducer;
